import { useContext, useEffect, useRef, useState } from 'react';
import { Preview } from '../../pages/Studio/Preview';
import clsx from 'clsx';
import { DisplayCanvas } from './DisplayCanvas';
import { PlaycastContext } from '../PlaycastWrapper';
import { Button } from '@playcastdotio/Shadcn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpToLine } from '@fortawesome/pro-solid-svg-icons';
import { Label } from '@radix-ui/react-context-menu';
import { faImage } from '@fortawesome/pro-light-svg-icons';
import { toast } from 'sonner';
import { blobToBase64 } from '../../api/util/conversions';
import { extractFilenameFromBase64 } from '../../utilities/extractFilenameFromBase64';

const maxSizeInMB = 10;
const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

export type Source = {
  label: 'Desktop' | 'Cameras' | 'Media' | 'Window Capture';
  choices: {
    label: string;
    value: string;
  }[];
};

type LocalProps = {
  showTitle?: boolean;
  showPicker?: boolean;
  height?: 'auto' | 'lg';
  selectedChoice?: string | null;
};

export const Display = ({ showTitle = true, showPicker = true, height = 'auto', selectedChoice }: LocalProps) => {
  const context = useContext(PlaycastContext);
  // const [selectedChoice, setSelectedChoice] = useState<string | null>('');
  const [isDisplaySelected, setIsDisplaySelected] = useState(false);
  const [displays, setDisplays] = useState(context.stateStores.displayState.displays);
  const [selectedDisplay, setSelectedDisplay] = useState<number>(context.stateStores.displayState.info.selectedDisplayId);
  const [isLive, setIsLive] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileNameFromBase64, setFileNameFromBase64] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    setFileNameFromBase64(extractFilenameFromBase64(context.stateStores.staticImageState.staticImageBase64));
  }, [context.stateStores.staticImageState.staticImageBase64]);

  const handleDisplayClick = (index: number) => {
    // map index to display id
    const displayId = displays[index].displayId;
    context.targetFunctions.changeDisplay(displayId);
  };

  const handleClickImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > maxSizeInBytes) {
      return toast(<div className='text-orange-600'>File size exceeds {maxSizeInMB}MB. Please upload a smaller file.</div>);
    }

    setFile(file);
    const base64 = await blobToBase64(file);
    context.targetFunctions.setStaticImage(base64);
  };

  useEffect(() => {
    setIsLive(context.stateStores.hostState.status === 'streaming');
  }, [context.stateStores.hostState.status]);

  useEffect(() => {
    setDisplays(context.stateStores.displayState.displays);
  }, [context.stateStores.displayState.displays]);

  useEffect(() => {
    setSelectedDisplay(context.stateStores.displayState.info.selectedDisplayId);
  }, [context.stateStores.displayState.info.selectedDisplayId]);

  useEffect(() => {
    if (selectedChoice?.startsWith('display')) {
      setIsDisplaySelected(true);
    } else {
      setIsDisplaySelected(false);
    }
  }, [selectedChoice]);

  useEffect(() => {
    const interval = setInterval(() => {
      const currentTime = Date.now();
      if (context.stateStores.hostState.startTime === 0) {
        setElapsedTime(0);
      } else {
        const elapsed = currentTime - context.stateStores.hostState.startTime;
        setElapsedTime(elapsed);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [context.stateStores.hostState.startTime]);

  const formatElapsedTime = (ms: number) => {
    // console.log(ms);
    const totalSeconds = Math.floor((ms ?? 1) / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours}h ${minutes}m ${seconds}s`;
  };

  return (
    <>
      {/* {showPicker && <DisplayPicker choiceState={[selectedChoice, setSelectedChoice]} />} */}

      <div className='theme-midnight:bg-slate-800 bg-darker border-primary overflow-hidden rounded-lg border-2'>
        <div className={clsx(height === 'auto' ? '' : '', 'flex size-full flex-1 flex-col justify-center')}>
          <div className={clsx(isDisplaySelected && displays.length > 1 ? 'h-72' : 'h-full', 'relative flex w-full justify-center')}>
            <div className='aspect-video size-auto'>
              <Preview />
            </div>

            {isLive && (
              <span className='absolute left-4 top-4 flex items-center gap-x-2 text-red-600'>
                <Label className='text-md font-semibold tabular-nums'>{formatElapsedTime(elapsedTime)}</Label>
              </span>
            )}

            {/* {isLive && (
              <span className='absolute right-4 top-4 flex items-center gap-x-2 text-red-600'>
                <div className='rounded-full border-2 border-red-600'>
                  <div className='m-1 size-2 rounded-full bg-red-600' />
                </div>
                <Label className='font-["Syne"] text-xl font-semibold'>LIVE</Label>
              </span>
            )} */}

            {selectedChoice === '1' && (
              <div className='absolute bottom-2 z-10 flex w-full items-center gap-x-2 px-2'>
                <FontAwesomeIcon icon={faImage} className='text-primary h-7 w-7' />
                <div className='flex w-full min-w-0 flex-col'>
                  <input type='file' ref={fileInputRef} className='hidden' onChange={handleFileChange} accept='image/*' />
                  <Button
                    onClick={handleClickImage}
                    className='border-midnight-dark m-0 w-full justify-start border bg-gray-900 px-2 text-white hover:bg-gray-900 focus:bg-gray-900 active:bg-gray-900'
                  >
                    <div className='flex w-full items-center'>
                      <Label className='cursor-pointer truncate text-sm font-normal'>
                        {file ? file.name : fileNameFromBase64 ? fileNameFromBase64 : 'No file chosen'}
                      </Label>
                      <FontAwesomeIcon icon={faArrowUpToLine} className='ml-auto h-4 w-4' />
                    </div>
                  </Button>
                </div>
              </div>
            )}
          </div>
          {isDisplaySelected && displays.length > 1 && (
            <div className='relative flex items-center justify-center bg-slate-950'>
              <DisplayCanvas displays={displays} onDisplayClick={handleDisplayClick} selectedId={selectedDisplay} showTitle={showTitle} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};
